/**
 * Pure-function client-side detector for collapsed-spacing text corruption.
 *
 * Mirror of `backend/utils/text_detection.py` — same algorithm, same
 * dictionaries, same thresholds, same severity buckets, so the editor
 * can flag suspicious text in real time without an API round-trip and
 * the backend can validate at save / import / AI-output boundaries.
 *
 * Hard contract:
 *   detectSuspiciousText(text)            -> Warning[]    (one string)
 *   detectSuspiciousInPages(pages)        -> ElementWarning[]
 *   summarizeWarnings(warnings)           -> Summary
 *
 * Pure. No DOM. No fetch. No side effects.
 *
 * The dictionaries below are the SAME entries as the Python detector.
 * When you add a compound there, add it here too — the JS test suite
 * + Python test suite both lock the behaviour to the same fixtures.
 */

// ── Thresholds ─────────────────────────────────────────────────────────
export const CONFIDENCE_SAFE      = 0.75;
export const AI_FLAG_THRESHOLD    = 0.92;
export const AI_SUGGEST_THRESHOLD = 0.98;

// ── Heuristic data (mirror of utils/text_repair.py) ────────────────────

const KNOWN_COMPOUNDS = {
  LIVINGROOM:            'LIVING ROOM',
  MODERNKITCHEN:         'MODERN KITCHEN',
  CARGARAGE:             'CAR GARAGE',
  EXCLUSIVELYOFFERED:    'EXCLUSIVELY OFFERED',
  EXCLUSIVELYOFFEREDAT:  'EXCLUSIVELY OFFERED AT',
  OPENFLOORPLAN:         'OPEN FLOOR PLAN',
  FLOORTOCEILINGWINDOWS: 'FLOOR TO CEILING WINDOWS',
  SMARTHOMETECHNOLOGY:   'SMART HOME TECHNOLOGY',
  PRIVATEBACKYARD:       'PRIVATE BACKYARD',
  HIGHENDFINISHES:       'HIGH END FINISHES',
  DININGROOM:            'DINING ROOM',
  MASTERBEDROOM:         'MASTER BEDROOM',
  WALKINCLOSET:          'WALK IN CLOSET',
  GOURMETKITCHEN:        'GOURMET KITCHEN',
  FLOORPLAN:             'FLOOR PLAN',
  OPENCONCEPT:           'OPEN CONCEPT',
  SMARTHOME:             'SMART HOME',
  HIGHEND:               'HIGH END',
  TWOSTORY:              'TWO STORY',
  FORSALE:               'FOR SALE',
  FORRENT:               'FOR RENT',
};

const WORD_DICTIONARY = new Set([
  'A', 'AN', 'AT', 'BY', 'FOR', 'IN', 'ON', 'OF', 'OR', 'THE', 'TO', 'WITH',
  'BATHROOM', 'BATHROOMS', 'BEDROOM', 'BEDROOMS', 'ROOM', 'ROOMS',
  'LIVING', 'DINING', 'KITCHEN', 'MASTER', 'GARAGE', 'CAR', 'CARS',
  'FLOOR', 'PLAN', 'SALE', 'RENT', 'HOME', 'HOMES', 'HOUSE',
  'HOUSES', 'PROPERTY', 'PROPERTIES', 'REALTY', 'REAL', 'ESTATE',
  'CONDO', 'APARTMENT', 'STUDIO', 'LOFT', 'TOWNHOUSE',
  'STORY', 'STORIES', 'FOOT', 'FEET', 'SQUARE', 'ACRE', 'ACRES',
  'MODERN', 'LUXURY', 'PREMIUM', 'EXCLUSIVE', 'EXCLUSIVELY',
  'OFFERED', 'AVAILABLE', 'SOLD', 'PENDING', 'NEW', 'UPDATED',
  'RENOVATED', 'BUILT', 'YEAR', 'PRICE', 'REDUCED',
  'GOURMET', 'OPEN', 'PRIVATE', 'SECURE', 'GATED', 'SMART',
  'TECHNOLOGY', 'HIGH', 'END', 'ENDS', 'FINISH', 'FINISHES',
  'BACKYARD', 'FRONTYARD', 'POOL', 'SPA', 'FIREPLACE', 'DECK',
  'PATIO', 'BALCONY', 'TERRACE', 'VIEW', 'VIEWS', 'WATERFRONT',
  'OCEAN', 'LAKE', 'MOUNTAIN', 'CITY', 'PARK',
  'WINDOWS', 'WINDOW', 'CEILING', 'CEILINGS', 'VAULTED',
  'WALK', 'CLOSET', 'PANTRY',
  'SCHEDULE', 'BOOK', 'CALL', 'TEXT', 'VISIT', 'TOUR',
  'DISCOVER', 'EXPLORE', 'FIND', 'DREAM', 'AWAITS', 'NOW', 'TODAY',
  'AND', 'BUT', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT',
]);

const RE_DIGIT_LETTER = /(\d)([A-Z])/g;
const RE_LETTER_DIGIT = /([A-Z])(\d)/g;

// ── Casing helpers ─────────────────────────────────────────────────────
const isAllUpper = (s) => s.length > 0 && s === s.toUpperCase() && s !== s.toLowerCase();
const isAllLower = (s) => s.length > 0 && s === s.toLowerCase() && s !== s.toUpperCase();
const titleCase = (s) =>
  s.replace(/\b\w+/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());

// ── Tile splitter (longest-match greedy over WORD_DICTIONARY) ─────────
const WORDS_SORTED_BY_LEN = [...WORD_DICTIONARY].sort((a, b) => b.length - a.length);

function splitAllCapsPhrase(token) {
  if (token.length < 8) return null;
  if (!/^[A-Z]+$/.test(token)) return null;
  const out = [];
  let pos = 0;
  const n = token.length;
  while (pos < n) {
    let matched = false;
    for (const w of WORDS_SORTED_BY_LEN) {
      if (w.length > n - pos) continue;
      if (token.substr(pos, w.length) === w) {
        out.push(w);
        pos += w.length;
        matched = true;
        break;
      }
    }
    if (!matched) return null;
  }
  if (out.length < 2) return null;
  const repaired = out.join(' ');
  if (repaired === token) return null;
  const avgLen = out.reduce((a, w) => a + w.length, 0) / out.length;
  // Same formula as Python: floor 0.80, cap 0.95, slope from avg-word-length.
  const confidence = Math.max(0.80, Math.min(0.95, 0.80 + (avgLen - 3) * 0.03));
  return { repaired, confidence };
}

// ── Single-token repair (mirrors _repair_token in Python) ──────────────
function repairToken(token) {
  if (!token) return null;
  const up = token.toUpperCase();

  // (1) dictionary
  if (Object.prototype.hasOwnProperty.call(KNOWN_COMPOUNDS, up)) {
    const cased = KNOWN_COMPOUNDS[up];
    let repaired;
    if (isAllUpper(token))       repaired = cased;
    else if (isAllLower(token))  repaired = cased.toLowerCase();
    else                         repaired = titleCase(cased);
    if (repaired === token) return null;
    return { repaired, confidence: 1.0, rule: 'dictionary' };
  }

  // (2) digit↔letter boundary — only inside ALL-CAPS labels
  if (isAllUpper(token) || (/^\d/.test(token) && isAllUpper(token.slice(1)))) {
    const rep = token
      .replace(RE_DIGIT_LETTER, '$1 $2')
      .replace(RE_LETTER_DIGIT, '$1 $2');
    if (rep !== token) return { repaired: rep, confidence: 0.92, rule: 'digit-letter' };
  }

  // (3) all-caps phrase tile
  const tile = splitAllCapsPhrase(token);
  if (tile) {
    return { repaired: tile.repaired, confidence: tile.confidence, rule: 'dictionary-tile' };
  }

  return null;
}

// ── Whole-string repair proposal (mirrors propose_repair in Python) ────
function proposeRepair(text) {
  if (!text || typeof text !== 'string') return null;
  const outLines = [];
  const rulesUsed = new Set();
  const confidences = [];
  for (const line of text.split('\n')) {
    if (!line) { outLines.push(line); continue; }
    const outTokens = [];
    for (const tok of line.split(' ')) {
      const proposal = repairToken(tok);
      if (proposal === null) outTokens.push(tok);
      else {
        outTokens.push(proposal.repaired);
        rulesUsed.add(proposal.rule);
        confidences.push(proposal.confidence);
      }
    }
    outLines.push(outTokens.join(' '));
  }
  const repaired = outLines.join('\n');
  if (repaired === text) return null;
  if (confidences.length === 0) return null;
  const rule = rulesUsed.size === 1 ? [...rulesUsed][0] : 'mixed';
  return { repaired, confidence: Math.min(...confidences), rule };
}

// ── Severity bucket ────────────────────────────────────────────────────
function severityFor(confidence) {
  if (confidence >= AI_SUGGEST_THRESHOLD) return 'high';
  if (confidence >= AI_FLAG_THRESHOLD)    return 'warn';
  return 'info';
}

// ── Public API ─────────────────────────────────────────────────────────

/**
 * @param {string} text
 * @returns {Array<{original, suggestion, confidence, rule, severity}>}
 *   Empty array means "looks clean".
 */
export function detectSuspiciousText(text) {
  if (!text || typeof text !== 'string') return [];
  const proposal = proposeRepair(text);
  if (proposal === null) return [];
  return [{
    original:   text,
    suggestion: proposal.repaired,
    confidence: proposal.confidence,
    rule:       proposal.rule,
    severity:   severityFor(proposal.confidence),
  }];
}

/**
 * Walk page → text element → {text, content} fields. Pure; never
 * mutates `pages`.
 */
export function detectSuspiciousInPages(pages) {
  const out = [];
  for (const page of (pages || [])) {
    const pageId = page?.id || page?.name || '?';
    for (const el of (page?.elements || [])) {
      if (el?.type !== 'text') continue;
      for (const field of ['text', 'content']) {
        const value = el?.[field];
        if (typeof value !== 'string' || !value) continue;
        const proposal = proposeRepair(value);
        if (!proposal) continue;
        out.push({
          page:       pageId,
          element:    el.id || '?',
          field,
          original:   value,
          suggestion: proposal.repaired,
          confidence: proposal.confidence,
          rule:       proposal.rule,
          severity:   severityFor(proposal.confidence),
        });
      }
    }
  }
  return out;
}

/**
 * Aggregate stats — used by the save-flow warning toast and the
 * import-validation report.
 */
export function summarizeWarnings(warnings) {
  if (!warnings || warnings.length === 0) {
    return { count: 0, by_severity: {}, by_rule: {}, max_confidence: 0, samples: [] };
  }
  const bySev = {}, byRule = {};
  let maxConf = 0;
  for (const w of warnings) {
    bySev[w.severity] = (bySev[w.severity] || 0) + 1;
    byRule[w.rule]    = (byRule[w.rule]    || 0) + 1;
    if (w.confidence > maxConf) maxConf = w.confidence;
  }
  const samples = [...warnings].sort((a, b) => b.confidence - a.confidence).slice(0, 5);
  return {
    count:          warnings.length,
    by_severity:    bySev,
    by_rule:        byRule,
    max_confidence: Math.round(maxConf * 10000) / 10000,
    samples,
  };
}
